import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { storage } from '../../lib/firebase';
import type { ThreadAttachment } from './types';
import {
  ThreadTempQuotaError,
  confirmThreadTempUpload,
  deleteThreadTempFiles,
  reserveThreadTempUpload,
} from './threadTempApi';
import { assertAttachmentFilesValid } from './attachmentValidation';
import { emitThreadTempQuotaExceeded } from './threadTempQuotaEvents';

/** 永久ファイル: 100MB */
export const MAX_PERMANENT_FILE_SIZE = 100 * 1024 * 1024;
/** 一時ファイル: 1GB（30日後に期限切れ） */
export const MAX_TEMPORARY_FILE_SIZE = 1024 * 1024 * 1024;

const sanitizeFilename = (name: string): string =>
  name.replace(/[\u0000-\u001f<>:"/\\|?*]+/g, '').slice(0, 200);

export interface UploadThreadTemporaryAttachmentParams {
  workspaceId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  file: File;
  onProgress?: (ratio: number) => void;
}

export interface UploadedThreadTemporaryAttachment extends ThreadAttachment {}

/**
 * スレッド添付ファイルを Firebase Storage にアップロードし、メタデータを返す。
 * - temporary: 30日後に期限切れとなるパスに保存。ワークスペース合計 10GB（全プラン共通）までを事前予約で制御
 */
export const uploadThreadTemporaryAttachment = async ({
  workspaceId,
  threadId,
  targetType,
  targetId,
  file,
  onProgress,
}: UploadThreadTemporaryAttachmentParams): Promise<UploadedThreadTemporaryAttachment> => {
  assertAttachmentFilesValid([{ file, kind: 'temporary' }]);

  // 容量（ワークスペース合計）の予約。超過時は ThreadTempQuotaError を投げ、画面側のモーダルへ通知する。
  // 予約 doc の存在とサイズ一致が Storage Rules の書き込み条件なので、これを経ない保存はできない。
  let reservation;
  try {
    reservation = await reserveThreadTempUpload({
      workspaceId,
      threadId,
      targetType,
      targetId,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
    });
  } catch (e) {
    if (e instanceof ThreadTempQuotaError) emitThreadTempQuotaExceeded(e);
    throw e;
  }
  const attachmentId = reservation.attachmentId;
  const storagePath = reservation.storagePath;
  const sanitized = storagePath.split('/').pop() || sanitizeFilename(file.name);

  const storageRef = ref(storage, storagePath);
  let downloadURL: string;
  try {
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || undefined,
    });

    const snapshot = await new Promise<typeof task.snapshot>((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => {
          try {
            const ratio = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
            onProgress?.(ratio);
          } catch { /* ignore */ }
        },
        reject,
        () => resolve(task.snapshot),
      );
    });

    downloadURL = await getDownloadURL(snapshot.ref);
  } catch (e) {
    // 予約だけ残って容量を占有し続けないよう、今回作った予約（と中途半端な保存物）を解放する。
    try {
      await deleteThreadTempFiles({ workspaceId, attachmentIds: [attachmentId] });
    } catch (releaseError) {
      console.warn('[uploadThreadTemporaryAttachment] failed to release reservation:', releaseError);
    }
    throw e;
  }
  // 確認（downloadURL の記録と確認済みフラグ）。失敗しても保存自体は完了しているので、期限掃除ジョブが後で補う。
  try {
    await confirmThreadTempUpload({ workspaceId, attachmentId, downloadURL });
  } catch (e) {
    console.warn('[uploadThreadTemporaryAttachment] confirm failed:', e);
  }

  const expiresAt = Timestamp.fromMillis(reservation.expiresAtMillis);

  return {
    id: attachmentId,
    fileName: sanitized,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    attachmentType: 'temporary',
    expiresAt,
  };
};

